import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import type { Page } from "@inertiajs/core";
import { router } from "@inertiajs/vue3";

export const OFFLINE_AUTH_CACHE_KEY = "engetel_offline_auth_snapshot_v1";

export type OfflineAuthSnapshot = {
  cachedAt: string;
  user: { id: number; name: string; email: string | null } | null;
  current_company?: unknown;
  current_workspace?: unknown;
};

export async function seedOfflineAuthFromInitialPage(page: Page): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    return;
  }
  const snapshot = buildSnapshot(page);
  if (snapshot) {
    await Preferences.set({
      key: OFFLINE_AUTH_CACHE_KEY,
      value: JSON.stringify(snapshot),
    });
  } else {
    await Preferences.remove({ key: OFFLINE_AUTH_CACHE_KEY });
  }
}

function buildSnapshot(page: Page): OfflineAuthSnapshot | null {
  const auth = page.props.auth as Record<string, unknown> | undefined;
  if (!auth) {
    return null;
  }
  const user = auth.user as Record<string, unknown> | null | undefined;
  if (!user?.id) {
    return null;
  }

  return {
    cachedAt: new Date().toISOString(),
    user: {
      id: Number(user.id),
      name: String(user.name ?? ""),
      email: user.email != null ? String(user.email) : null,
    },
    current_company: auth.current_company ?? undefined,
    current_workspace: auth.current_workspace ?? undefined,
  };
}

export async function getOfflineCachedAuthSnapshot(): Promise<OfflineAuthSnapshot | null> {
  if (!Capacitor.isNativePlatform()) {
    return null;
  }
  const { value } = await Preferences.get({ key: OFFLINE_AUTH_CACHE_KEY });
  if (!value) {
    return null;
  }
  try {
    return JSON.parse(value) as OfflineAuthSnapshot;
  } catch {
    return null;
  }
}

export async function clearOfflineCachedAuthSnapshot(): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    return;
  }
  await Preferences.remove({ key: OFFLINE_AUTH_CACHE_KEY });
}

let unregister: VoidFunction | undefined;

/**
 * Após cada visita Inertia com sucesso no app nativo, grava um snapshot leve do utilizador
 * em @capacitor/preferences (UI / “quem sou” quando não há rede).
 * A sessão real continua a ser cookie + Laravel; SQLite do servidor não é copiado para o telemóvel.
 */
export function registerMobileOfflineAuthCache(): void {
  if (!Capacitor.isNativePlatform()) {
    return;
  }
  unregister?.();

  unregister = router.on("success", (page) => {
    void (async () => {
      const snapshot = buildSnapshot(page);
      if (snapshot) {
        await Preferences.set({
          key: OFFLINE_AUTH_CACHE_KEY,
          value: JSON.stringify(snapshot),
        });
      } else {
        await Preferences.remove({ key: OFFLINE_AUTH_CACHE_KEY });
      }
    })();
  });
}
